import {
  type Component,
  type MaybeRef,
  type Ref,
  computed,
  reactive,
  ref,
  shallowRef,
  toValue,
} from 'vue'

import type { Price } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment'
import { RevButton } from '@ds/components/Button'

import type {
  PaymentFormAdapterFullProps as AdapterProps,
  PaymentFormAdapter,
  PaymentFormAdapterCreateFunction,
  PaymentFormAdapterInstance,
} from '../../../form-common/types'
import {
  PAYMENT_FORM_ADAPTERS,
  PAYMENT_FORM_ADAPTER_NOT_REQUIRED,
} from '../../config/adapters'

type UsePaymentAdapter = {
  // Reactive options used to build the adapter props
  formId: MaybeRef<string>
  basePrice: MaybeRef<Price>
  createPayment: MaybeRef<PaymentFormAdapterCreateFunction>
  paymentMethod: MaybeRef<PaymentMethod | null>
  disabled?: MaybeRef<boolean>

  isPaymentRequired: MaybeRef<boolean>

  // Events handlers
  onSetupStart: AdapterProps['onSetupStart']
  onSetupSuccess: AdapterProps['onSetupSuccess']
  onSetupError: AdapterProps['onSetupError']

  onSubmitStart: AdapterProps['onSubmitStart']
  onSubmitSuccess: AdapterProps['onSubmitSuccess']
  onSubmitError: AdapterProps['onSubmitError']
}

export const usePaymentForm = ({
  formId,
  basePrice,
  createPayment,
  disabled,
  paymentMethod,
  isPaymentRequired,
  ...events
}: UsePaymentAdapter) => {
  const isSettingUp = ref(false)
  const isSubmitting = ref(false)
  const isBusy = computed(() => isSettingUp.value || isSubmitting.value)

  const onSetupStart: AdapterProps['onSetupStart'] = (...args) => {
    isSettingUp.value = true
    events.onSetupStart(...args)
  }

  const onSetupSuccess: AdapterProps['onSetupSuccess'] = (...args) => {
    isSettingUp.value = false
    events.onSetupSuccess(...args)
  }

  const onSetupError: AdapterProps['onSetupError'] = (...args) => {
    isSettingUp.value = false
    events.onSetupError(...args)
  }

  const onSubmitStart: AdapterProps['onSubmitStart'] = (...args) => {
    isSubmitting.value = true
    events.onSubmitStart(...args)
  }

  const onSubmitSuccess: AdapterProps['onSubmitSuccess'] = (...args) => {
    isSubmitting.value = false
    events.onSubmitSuccess(...args)
  }

  const onSubmitError: AdapterProps['onSubmitError'] = (...args) => {
    isSubmitting.value = false
    events.onSubmitError(...args)
  }

  const instance = shallowRef<PaymentFormAdapterInstance | null>(null)
  const refCallback = (instanceRef: PaymentFormAdapterInstance) => {
    instance.value = instanceRef
  }

  return {
    component: reactive({
      is: computed((): PaymentFormAdapter => {
        if (!toValue(isPaymentRequired)) {
          return PAYMENT_FORM_ADAPTER_NOT_REQUIRED
        }

        const method = toValue(paymentMethod)

        if (!method) {
          // NOOP adapter
          // To avoid having to check if the component is defined before rendering it
          return () => null
        }

        const { pspCode, bmCode, integrationType } = method

        const type = integrationType ?? 'DEFAULT'

        return (
          PAYMENT_FORM_ADAPTERS[pspCode][bmCode]?.[type] ||
          PAYMENT_FORM_ADAPTERS[pspCode][bmCode]?.DEFAULT ||
          PAYMENT_FORM_ADAPTERS[pspCode].DEFAULT[type] ||
          PAYMENT_FORM_ADAPTERS[pspCode].DEFAULT.DEFAULT
        )
      }),
      props: {
        id: formId,
        basePrice,
        createPayment,
        disabled: computed(() => toValue(disabled) || isBusy.value),
        paymentMethod,

        onSetupStart,
        onSetupSuccess,
        onSetupError,

        onSubmitStart,
        onSubmitSuccess,
        onSubmitError,

        ref: refCallback,
      },
    }) satisfies {
      is: PaymentFormAdapter
      props: AdapterProps
    },

    instance,
    isSubmitting,
    isBusy,
  }
}

const useSubmitButton = (
  { paymentMethod, formId }: UsePaymentAdapter,
  instance: Ref<PaymentFormAdapterInstance | null>,
) => {
  type SubmitButtonPropsType = Partial<
    InstanceType<typeof RevButton>['$props']
  > &
    Record<string, unknown>

  return {
    component: reactive({
      is: computed(() => instance.value?.submitButton?.is ?? RevButton),
      props: computed((): SubmitButtonPropsType => {
        const method = toValue(paymentMethod)

        return {
          'data-qa': 'submit-button',
          'data-qa-architecture': method?.architecture,
          'data-qa-bm-code': method?.bmCode,
          'data-qa-integration-type': method?.integrationType,
          'data-qa-psp-code': method?.pspCode,
          type: 'submit',
          'full-width': 'responsive',
          variant: 'primary',
          form: toValue(formId),
          ...instance.value?.submitButton?.props,
        }
      }),
    }) satisfies {
      is: Component
      props: SubmitButtonPropsType
    },
  }
}

export const usePaymentFormAdapter = (options: UsePaymentAdapter) => {
  const form = usePaymentForm(options)
  const submitButton = useSubmitButton(options, form.instance)
  const loadingMessage = computed(() =>
    toValue(form.instance.value?.loadingMessage),
  )

  return { form, submitButton, loadingMessage }
}
